import { useMutation, useQuery } from "@tanstack/react-query";
import cx from "classnames";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import {
  ActionFunction,
  LoaderFunction,
  redirect,
  useActionData,
  useLoaderData,
  useNavigate,
  useRevalidator,
  useSearchParams,
  useSubmit,
} from "react-router-dom";

import { cartMeetsMinimumSpendRequirementForReward } from "@/modules/cart";
import {
  invalidateCurrentOrderDetails,
  useCurrentOrderDetails,
} from "@/modules/cart/useCartDetails";
import { get, post } from "api";
import { DiscountAppliesTo, RewardStatus } from "api/types";
import BackgroundPattern from "assets/images/BackgroundPattern.svg";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import RewardOpen from "atoms/icon/icons/ic_open_reward_small_ol.svg";
import Info from "atoms/icon/icons/info.svg";
import QrCode from "atoms/icon/icons/qr_code.svg";
import RepeatCash from "atoms/icon/icons/RepeatCash.svg";
import Rewards from "atoms/icon/icons/rewards_nav.svg";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import TopNav from "molecules/navbars/TopNav";
import { ItemAppliedToCartModal } from "organisms/item-applied-to-cart-modal/ItemAppliedToCartModal";
import { ItemNotAppliedToCartModal } from "organisms/item-not-applied-to-cart-modal/ItemNotAppliedToCartModal";
import { RedeemedFreePackageGroupModal } from "organisms/redeemed-free-package-group-modal/RedeemedFreePackageGroupModal";
import { RewardDetails } from "organisms/reward-details/RewardDetails";
import { FreeServiceSelector } from "organisms/selection-modals/free-service-selector/FreeServiceSelector";
import { queryClient } from "pages/Root";
import { formatCurrency } from "toolbox/Money";
import { waitObj } from "toolbox/Promise";

import { ConfirmBankModal } from "../ConfirmBankModal";
import { InStoreRedeemedModal } from "../InStoreRedeemedModal";
import { SuccessfullyBankedModal } from "../SuccessfullyBankedModal";

const RewardBanner = ({
  reward: { status, title },
}: Pick<LoaderData, "reward">) => {
  return (
    <div className="relative flex flex-col items-center bg-brand-color bg-[image:linear-gradient(82deg,transparent,rgb(255,255,255,.2)_34%,rgb(255,255,255,.2)_46%,transparent)] px-4 py-11 text-center text-white">
      <BackgroundPattern className="absolute inset-0 w-full opacity-10" />
      <Icon
        color="white"
        svg={
          status === RewardStatus.Available
            ? Rewards
            : status === RewardStatus.CashedIn
            ? RepeatCash
            : RewardOpen
        }
        size="xsmall"
      />
      <span className="mt-4 text-sub2 uppercase">
        Reward{" "}
        {status === RewardStatus.Available
          ? "Unlocked"
          : status === RewardStatus.Redeemed
          ? "Redeemed"
          : "Banked"}
        !
      </span>
      <h1 className="mt-6 font-serif text-h1">{title}</h1>
    </div>
  );
};

type ActionResult = {
  redeemSuccess: boolean;
  bankSuccess: boolean;
};

const load = async (rewardId: string, request: Request) => {
  const url = new URL(request.url);
  const comingFromScan = url.searchParams.get("comingFromScan");
  const platformUserRewardId = url.searchParams.get("platformUserRewardId");
  const referralRewardId = url.searchParams.get("referralRewardId");

  const reward = queryClient.fetchQuery(
    ["/rewards", { rewardId, platformUserRewardId }],
    () =>
      get("/rewards/{rewardId}", {
        rewardId,
        ...(platformUserRewardId && { platformUserRewardId }),
      })
  );

  const inclusions = queryClient.fetchQuery(
    ["/rewards/inclusions", { rewardId, referralRewardId }],
    () =>
      get("/rewards/{rewardId}/inclusions", {
        rewardId,
        ...(referralRewardId && { referralRewardId }),
      })
  );

  const userInfo = queryClient.fetchQuery(["/user"], () => get("/user"), {
    staleTime: 1000 * 60 * 2,
  });

  const authClaims = queryClient.fetchQuery(["/auth/claims"], () =>
    get("/auth/claims")
  );

  const isAdminImpersonation = authClaims.then(
    (response) => !!response?.claims.adminUserId
  );

  const userMemberships = queryClient.fetchQuery(
    ["/platform-user-memberships"],
    () => get("/platform-user-memberships")
  );

  const listings = queryClient.fetchQuery(["/listings"], () =>
    get("/listings")
  );

  const referralRewardCampaigns = referralRewardId
    ? queryClient.fetchQuery(
        ["/referral-rewards", referralRewardId],
        async () => {
          const user = await userInfo;
          if (!user.platform?.id) {
            return [];
          }
          return get("/referral-rewards/{platformId}/location/{locationId}", {
            platformId: user.platform.id,
            locationId: user.platformLocation?.id ?? "",
          });
        }
      )
    : Promise.resolve([]);

  return waitObj({
    reward,
    repeatCash: userInfo.then((u) => u.repeatCashBalance),
    comingFromScan: comingFromScan == "true" ? true : false,
    isAdminImpersonation,
    inclusions,
    listings,
    userMemberships,
    referralRewardCampaigns,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const rewardsLoader: LoaderFunction = ({
  params: { rewardId },
  request,
}) => {
  if (!rewardId) {
    return redirect("..");
  }
  return load(rewardId, request);
};

export const rewardsAction: ActionFunction = async ({ params, request }) => {
  if (!params.rewardId) {
    throw new Error("Missing route data");
  }
  const formData = await request.formData();
  const shouldRedeem = formData.get("type") === "redeem";

  const url = new URL(request.url);
  const referralRewardId = url.searchParams.get("referralRewardId");
  const platformUserRewardId = url.searchParams.get("platformUserRewardId");

  try {
    if (shouldRedeem) {
      const reward = await post("/rewards/redeem", {
        rewardId: params.rewardId,
        referralRewardId,
      });

      if (reward.isInStoreRedeemable && reward.discount?.appliesTo === 1) {
        invalidateCurrentOrderDetails();
      }

      return { redeemSuccess: true };
    }

    const cashReward = await post("/rewards/cash", {
      rewardId: params.rewardId,
      referralRewardId,
      platformUserRewardId: platformUserRewardId,
    });

    if (cashReward) {
      queryClient.invalidateQueries({
        queryKey: ["/user"],
      });
      invalidateCurrentOrderDetails();
    }

    return { bankSuccess: true };
  } catch (error: any) {
    //TODO: Refactor to prevent doing string comparison
    if (
      error.response.status === 400 &&
      error.response.data === "itemsInCartNotIncludedInDiscount"
    ) {
      return { redeemSuccess: false };
    }
  }
};

const RewardsPage = () => {
  const navigate = useNavigate();
  const {
    reward,
    repeatCash,
    comingFromScan,
    isAdminImpersonation,
    inclusions,
    userMemberships,
    listings,
    referralRewardCampaigns,
  } = useLoaderData() as LoaderData;
  const data = useActionData() as ActionResult | undefined;
  const submit = useSubmit();
  const [showSuccessfulRedemptionModal, setShowSuccessfulRedemptionModal] =
    useState(false);
  const [showBankRewardModal, setShowBankRewardModal] = useState<
    "confirmBank" | "successfullyBanked" | false
  >(false);
  const [showFreeServiceSelector, setShowFreeServiceSelector] = useState(false);
  const [selectedInclusionsToRedeem, setSelectedInclusionsToRedeem] = useState<
    string[]
  >([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const rewardHasMultipleInclusions =
    reward.discount?.appliesTo == DiscountAppliesTo.AppliesToAll ||
    (reward.discount?.inclusions && reward.discount?.inclusions?.length > 1);
  const [showInclusionsModal, setShowInclusionsModal] = useState(false);
  const revalidator = useRevalidator();

  const { data: memberships } = useQuery({
    queryKey: ["/memberships"],
    queryFn: () => get("/memberships"),
  });

  useEffect(() => {
    setShowSuccessfulRedemptionModal(
      data?.redeemSuccess || comingFromScan ? true : false
    );
    if (
      data &&
      !data?.redeemSuccess &&
      rewardHasMultipleInclusions &&
      !showBankRewardModal
    ) {
      setShowInclusionsModal(true);
    }
  }, [data?.redeemSuccess, comingFromScan, rewardHasMultipleInclusions, data]);

  useEffect(() => {
    setShowBankRewardModal(
      data?.bankSuccess && repeatCash > 0 ? "successfullyBanked" : false
    );
  }, [data?.bankSuccess, repeatCash]);

  // Check if this is an InOffice referral reward
  const referralRewardCampaign = reward.referralRewardId
    ? referralRewardCampaigns.find(
        (campaign) =>
          campaign.currentPatientReferralReward.referralRewardId.value ===
            reward.referralRewardId ||
          campaign.newPatientReferralReward.referralRewardId.value ===
            reward.referralRewardId
      )
    : null;

  const isInOfficeReferralReward =
    referralRewardCampaign?.campaignValidity === 1;
  // For InOffice referral rewards, always treat as in-store redeemable to show QR code
  const inStoreRedeemable =
    reward.isInStoreRedeemable || isInOfficeReferralReward;
  const isRewardForFreeService = reward.freePackageGroup !== null;

  const closeSuccessfulRedemptionModal = () => {
    setShowSuccessfulRedemptionModal(false);
    if (searchParams.has("comingFromScan")) {
      searchParams.delete("comingFromScan");
      setSearchParams(searchParams, { replace: true });

      // We need to modify browser history to remove the current entry
      // and when the user clicks back, they are taken to the previous page
      if (window.history.length > 1) {
        window.history.go(-1);
      }
    }
  };

  const closeBankRewardModal = () => {
    setShowBankRewardModal(false);
  };

  const handleAdminRedeemInStoreReward = useMutation({
    mutationFn: async () => {
      await post("/rewards/redeem-in-store", {
        rewardId: reward.rewardId,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([
        "/rewards",
        { rewardId: reward.rewardId },
      ]);
      revalidator.revalidate();
      setShowSuccessfulRedemptionModal(true);
    },
  });

  const handleRedeemFreePackageGroupReward = async (
    freePackageGroupInclusionIds: string[]
  ) => {
    if (!reward.freePackageGroup) return;
    setSelectedInclusionsToRedeem(freePackageGroupInclusionIds);
    await post("/free-package-groups/{freePackageGroupId}/redeem", {
      freePackageGroupId: reward.freePackageGroup.id,
      freePackageGroupInclusionIds,
    });
  };

  const redeemFreePackageGroup = useMutation({
    mutationFn: handleRedeemFreePackageGroupReward,
    onSuccess: async () => {
      // Revalidate the reward details query to update the reward status
      revalidator.revalidate();
      // show success modal
      setShowSuccessfulRedemptionModal(true);
    },
    onSettled: async () => {
      if (setShowFreeServiceSelector) setShowFreeServiceSelector(false);
    },
  });

  const isActiveMemberOfMembershipInclusion = userMemberships.some(
    (membership) =>
      inclusions.memberships.some(
        (inclusion) => inclusion.id === membership.membership.id
      ) && membership.canceled === false
  );

  const isMemberOfSingleMembershipInclusion =
    inclusions.memberships.length === 1 &&
    inclusions.treatments.length == 0 &&
    inclusions.packages.length == 0 &&
    isActiveMemberOfMembershipInclusion;

  const onRedeemFreePackageGroup = () => {
    if (!reward.freePackageGroup?.freePackageGroupInclusions) return;
    const isChooseFrom = reward.freePackageGroup.hasSelectableInclusions;
    if (isChooseFrom) {
      setShowFreeServiceSelector(true);
    } else {
      redeemFreePackageGroup.mutate(
        reward.freePackageGroup?.freePackageGroupInclusions.map((i) => i.id)
      );
    }
  };

  const packagesFromListings = listings.flatMap((listing) => listing.packages);

  const itemsForSelection =
    reward.freePackageGroup?.freePackageGroupInclusions
      .filter(
        ({ membershipId, packageId }) =>
          membershipId !== null || packageId !== null
      )
      .map((inclusion) => {
        if (inclusion.membershipId) {
          const membership = memberships?.find(
            ({ id }) => id === inclusion.membershipId
          );
          return {
            name: membership?.name ?? "",
            quantity: inclusion.quantity,
            unitType: "month",
            id: inclusion.id,
          };
        }

        const listingPackage = packagesFromListings.find(
          ({ id }) => id === inclusion.packageId
        );
        const packageUnitType =
          inclusion.quantity < 2
            ? listingPackage?.packageUnitType.singularUnitType
            : listingPackage?.packageUnitType.pluralUnitType;
        return {
          name: listingPackage?.name ?? "",
          quantity: inclusion.quantity,
          unitType: packageUnitType ?? "",
          id: inclusion.id,
        };
      }) ?? [];

  const onFreeServicesSelected = (selectedInclusions: string[]) => {
    redeemFreePackageGroup.mutate(selectedInclusions);
  };

  const getButtonLabelForInAppRedeem = () => {
    if (isMemberOfSingleMembershipInclusion) return "You are already a member";
    return "Apply reward to cart";
  };

  const hasMembershipInclusions =
    !!reward.freePackageGroup?.freePackageGroupInclusions.some(
      (inclusion) => inclusion.membershipId !== null
    );

  const { data: cartDetails } = useCurrentOrderDetails();

  const cartMeetsMinimumSpend = cartMeetsMinimumSpendRequirementForReward(
    cartDetails?.estimatedTotal ?? 0,
    reward
  );

  const renderModals = () => (
    <>
      {inStoreRedeemable && showSuccessfulRedemptionModal && (
        <InStoreRedeemedModal onClose={closeSuccessfulRedemptionModal} />
      )}
      {!inStoreRedeemable && showSuccessfulRedemptionModal && (
        <>
          {isRewardForFreeService ? (
            <RedeemedFreePackageGroupModal
              itemType="reward"
              itemName={reward.title}
              onClose={closeSuccessfulRedemptionModal}
              redeemedInclusions={itemsForSelection.filter((inclusion) =>
                selectedInclusionsToRedeem.includes(inclusion.id)
              )}
              hasMembershipInclusions={hasMembershipInclusions}
            />
          ) : (
            <ItemAppliedToCartModal
              itemType="reward"
              itemName={reward.title}
              onClose={closeSuccessfulRedemptionModal}
            />
          )}
        </>
      )}
      {rewardHasMultipleInclusions && showInclusionsModal && (
        <ItemNotAppliedToCartModal
          itemType="reward"
          onClose={() => {
            setShowInclusionsModal(false);
          }}
        />
      )}
      {showBankRewardModal === "confirmBank" && (
        <ConfirmBankModal
          valueIfBanked={reward.valueIfBanked}
          onBankReward={() => {
            const formData = new FormData();
            formData.append("type", "bank");

            submit(formData, { method: "post" });
          }}
          onClose={closeBankRewardModal}
        />
      )}
      {showBankRewardModal === "successfullyBanked" && (
        <SuccessfullyBankedModal
          repeatCash={repeatCash}
          onClose={closeBankRewardModal}
        />
      )}
      {!!showFreeServiceSelector &&
        reward.freePackageGroup?.hasSelectableInclusions &&
        !!reward.freePackageGroup?.numberOfSelections && (
          <FreeServiceSelector
            open={showFreeServiceSelector}
            onClose={() => setShowFreeServiceSelector(false)}
            size="fullscreen"
            title={"Redeem Your Reward"}
            items={itemsForSelection}
            numberOfSelections={reward.freePackageGroup.numberOfSelections}
            onFirstButtonClick={onFreeServicesSelected}
            firstButtonLabel={"Confirm"}
            onSecondButtonClick={() => setShowFreeServiceSelector(false)}
            secondButtonLabel={"Cancel"}
            itemType={"reward"}
          />
        )}
    </>
  );

  const renderButtons = () => {
    if (!cartMeetsMinimumSpend) {
      return (
        <div className="flex w-full flex-col items-center gap-6 px-4">
          <Button
            fullWidth
            size="large"
            onClick={() => navigate("/shop")}
            data-testid="button-redeem-reward"
          >
            Shop now
          </Button>
        </div>
      );
    }

    return (
      <div
        className={cx(
          "flex flex-col items-center gap-4 px-4",
          reward.status === RewardStatus.Available && !reward.termsAndConditions
            ? "mt-6"
            : ""
        )}
      >
        {reward.status === RewardStatus.Available && (
          <>
            {inStoreRedeemable && isAdminImpersonation && (
              <Button
                fullWidth
                size="large"
                onClick={() => handleAdminRedeemInStoreReward.mutateAsync()}
                data-testid="button-redeem-reward"
              >
                Redeem reward
              </Button>
            )}

            {!inStoreRedeemable && !isRewardForFreeService && (
              <Button
                fullWidth
                size="large"
                disabled={
                  isMemberOfSingleMembershipInclusion ||
                  !!(data?.redeemSuccess || data?.bankSuccess)
                }
                onClick={() => {
                  const formData = new FormData();
                  formData.append("type", "redeem");
                  submit(formData, {
                    method: "post",
                  });
                }}
                data-testid="button-redeem-reward"
              >
                {getButtonLabelForInAppRedeem()}
              </Button>
            )}

            {inStoreRedeemable && !isAdminImpersonation && (
              <Button
                fullWidth
                size="large"
                onClick={() => {
                  navigate(`/scan-redeem-reward-in-location`, {
                    state: {
                      rewardId: reward.rewardId,
                    },
                  });
                }}
                data-testid="button-redeem-reward"
              >
                <Icon svg={QrCode} size="xsmall" color="white" />
                <span className="ml-1">Redeem reward</span>
              </Button>
            )}

            {!inStoreRedeemable && isRewardForFreeService && (
              <Button
                fullWidth
                size="large"
                onClick={onRedeemFreePackageGroup}
                data-testid="button-redeem-reward"
                disabled={redeemFreePackageGroup.isLoading}
              >
                <span className="ml-1">Redeem reward</span>
              </Button>
            )}

            {reward.repeatCashEnabled && (
              <Button
                fullWidth
                size="large"
                style="secondary"
                onClick={() => setShowBankRewardModal("confirmBank")}
              >
                <Icon svg={RepeatCash} size="xsmall" />
                <span className="ml-1">
                  Bank for{" "}
                  {reward.valueIfBanked
                    ? formatCurrency(reward.valueIfBanked)
                    : "N/A"}{" "}
                  in RepeatCash
                </span>
              </Button>
            )}
          </>
        )}
      </div>
    );
  };

  const renderMessages = () => (
    <>
      {reward.status === RewardStatus.CashedIn && (
        <div className="mx-4 mt-4 flex cursor-pointer items-center justify-center rounded bg-two py-5 text-center">
          <Icon svg={RepeatCash} size="small" />
          <div className="ml-2 text-title font-normal text-primary">
            Banked for{" "}
          </div>
          <div className="mx-1 text-title text-primary">
            {reward.valueIfBanked
              ? formatCurrency(reward.valueIfBanked)
              : "N/A"}
          </div>
          <div className="text-title font-normal text-primary">cash</div>
        </div>
      )}
      {reward.status === RewardStatus.Redeemed && !inStoreRedeemable && (
        <div className="mx-4 mt-4 flex items-center justify-center rounded bg-two px-4 py-5">
          <div>
            <Icon svg={RewardOpen} size="small" color="brandColor" />
          </div>
          <div className="ml-4 text-bold1">
            Congratulations! This reward has already been redeemed
          </div>
        </div>
      )}
      {(reward.status === RewardStatus.Redeemed ||
        reward.status === RewardStatus.CashedIn) && (
        <>
          <div className="py-4 text-center text-body1 text-primary">
            {reward.status === RewardStatus.Redeemed ? "Redeemed" : "Banked"}:{" "}
            {dayjs(reward.redeemedDateTime).format("ddd, L - hh:mma")}
          </div>

          {reward.status === RewardStatus.Redeemed &&
            reward.rewardPromoCode &&
            inStoreRedeemable && (
              <div className="mx-4 flex cursor-pointer items-center justify-center rounded bg-two p-4 text-center">
                <div className="flex items-center">
                  <div className="flex items-center">
                    <Icon svg={Info} size="small" color="secondaryText" />
                  </div>
                  <div className="mr-6 pl-2 text-left text-body1 text-primary">
                    Please show this page in-office to a staff member to book
                    your reward
                  </div>
                </div>
              </div>
            )}
        </>
      )}
      {reward.status === RewardStatus.Available && !inStoreRedeemable && (
        <div className="m-4">
          <RewardDetails
            inclusions={inclusions}
            discount={reward.discount ?? undefined}
            freePackageGroup={reward.freePackageGroup ?? undefined}
            meetsRewardMinSpendRequirement={cartMeetsMinimumSpend}
            rewardMinimumSpendInCents={reward.minimumSpendInCents}
          />
        </div>
      )}
      {reward.status === RewardStatus.Redeemed && inStoreRedeemable && (
        <div className="mx-4">
          <div className="mb-4 mt-6 text-center text-body1 text-secondary">
            Reward successfully redeemed in store.
          </div>
          <div className="mb-5 text-center text-body1 text-secondary">
            In store code: {reward.rewardPromoCode}
          </div>
        </div>
      )}
      {reward.status === RewardStatus.CashedIn && (
        <div className="mx-8 mb-5 text-center text-body1 text-secondary">
          Banked RepeatCash can be put towards in-app purchases. Explore shop
          now to spend this balance and save on treatments.
        </div>
      )}
      {reward.status === RewardStatus.Available && inStoreRedeemable && (
        <div className="mx-4 mt-6 flex items-center rounded bg-two px-3 py-2.5">
          <Icon svg={Info} color="secondaryText" size="xsmall" />
          <div className="ml-3 text-body2 text-primary">
            This reward applies to in-office purchases only. Redeem by scanning
            the QR code in store.
          </div>
        </div>
      )}
      {reward.status === RewardStatus.Available &&
        reward.termsAndConditions && (
          <p className="px-4 pb-6 text-center text-body2 italic text-secondary">
            {reward.termsAndConditions}
          </p>
        )}

      {reward.status === RewardStatus.Redeemed && !inStoreRedeemable && (
        <p className="px-4 pb-6 text-center text-body2 italic text-secondary">
          Reward can only be used once
        </p>
      )}
    </>
  );

  return (
    <div className="relative flex min-h-full flex-col">
      <TopNav startIconSVG={ChevronLeft} onStartIconClick={() => navigate(-1)}>
        <div className="flex max-h-[32px] items-center">
          <h1 className="text-xl">Rewards</h1>
        </div>
      </TopNav>

      <RewardBanner reward={reward} />

      {renderMessages()}

      {renderButtons()}

      {renderModals()}

      <div className="mt-auto">
        <RepeatMDFooter />
      </div>
    </div>
  );
};

export default RewardsPage;
